import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../app/store';
import { v4 as uuidv4 } from 'uuid';
import { DEVICE, DeviceType } from '../constants/deviceType';

export interface SettingsState {
  socketConnected: boolean;
  socketAddress: string;
  deviceType: DeviceType;
  deviceId: number;
  uuid: string;
  visualEffects: boolean;
  forceReconnectOnLogin: boolean;
  monitorInitialDelay?: number; // ms before scroll starts
  monitorScrollSpeed?: number; // ms per scroll step
  monitorPostScrollDelay?: number; // ms after scroll before rotate
  monitorFontFamily?: string; // Font family for Monitor and MC
  selectedHall?: number | 'all'; // Which hall this device is assigned to (default: 'all')
  sendVisualControl?: boolean; // Whether this device auto-sends heats to LED/Broadcast screens
}

const initialState: SettingsState = {
  socketConnected: false,
  socketAddress: '',
  deviceType: DEVICE.JUDGE,
  deviceId: 0,
  visualEffects: true,
  forceReconnectOnLogin: false,
  uuid: uuidv4(),
  monitorInitialDelay: 3000,
  monitorScrollSpeed: 16,
  monitorPostScrollDelay: 3000,
  monitorFontFamily: 'Roboto Condensed Variable, sans-serif',
  selectedHall: 'all',
  sendVisualControl: false,
};

export const clearAll = createAsyncThunk(
  'settings/clear',
  async function (_payload, thunkAPI) {
    thunkAPI.dispatch({ type: 'CLEAR_ALL' });
    // console.log('cleared state');
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    changeSocketConnected(state, action: PayloadAction<boolean>) {
      state.socketConnected = action.payload;
    },
    changeSocketAddress(state, action: PayloadAction<string>) {
      state.socketAddress = action.payload;
    },
    changeDeviceType(state, action: PayloadAction<DeviceType>) {
      state.deviceType = action.payload;
    },
    changeDeviceId(state, action: PayloadAction<number>) {
      state.deviceId = action.payload;
    },
    changeVisualEffects(state, action: PayloadAction<boolean>) {
      state.visualEffects = action.payload;
    },
    changeForceReconnectOnLogin(state, action: PayloadAction<boolean>) {
      state.forceReconnectOnLogin = action.payload;
    },
    changeMonitorInitialDelay(state, action: PayloadAction<number>) {
      state.monitorInitialDelay = action.payload;
    },
    changeMonitorScrollSpeed(state, action: PayloadAction<number>) {
      state.monitorScrollSpeed = action.payload;
    },
    changeMonitorPostScrollDelay(state, action: PayloadAction<number>) {
      state.monitorPostScrollDelay = action.payload;
    },
    changeMonitorFontFamily(state, action: PayloadAction<string>) {
      state.monitorFontFamily = action.payload;
    },
    changeSelectedHall(state, action: PayloadAction<number | 'all'>) {
      state.selectedHall = action.payload;
    },
    changeSendVisualControl(state, action: PayloadAction<boolean>) {
      state.sendVisualControl = action.payload;
    }
  }
});

// Export actions
export const {
  changeSocketAddress,
  changeDeviceType,
  changeDeviceId,
  changeSocketConnected,
  changeVisualEffects,
  changeForceReconnectOnLogin,
  changeMonitorInitialDelay,
  changeMonitorScrollSpeed,
  changeMonitorPostScrollDelay,
  changeMonitorFontFamily,
  changeSelectedHall,
  changeSendVisualControl
} = settingsSlice.actions;

//selectors
export const selectSocketConnected = (state: RootState) => {
  // console.log(state);
  return state.settings.socketConnected;
};
export const selectSocketAddress = (state: RootState) => {
  if (state.settings.socketAddress) {
    return state.settings.socketAddress;
  }
  
  // Default to http protocol and port 3001 using the same hostname where the app is served
  const hostname = window.location.hostname;
  return `http://${hostname}:3001`;
};
export const selectDeviceType = (state: RootState) => {
  return state.settings.deviceType;
};
export const selectIsTrainingMode = (state: RootState) => {
  return state.settings.deviceType === DEVICE.TRAINING;
};
export const selectDeviceId = (state: RootState) => {
  return state.settings.deviceId;
};
export const selectDeviceUuid = (state: RootState) => {
  return state.settings.uuid;
};
export const selectVisualEffects = (state: RootState) => {
  return state.settings.visualEffects;
};
export const selectForceReconnectOnLogin = (state: RootState) => {
  return state.settings.forceReconnectOnLogin;
};
export const selectMonitorInitialDelay = (state: RootState) => state.settings.monitorInitialDelay ?? 3000;
export const selectMonitorScrollSpeed = (state: RootState) => state.settings.monitorScrollSpeed ?? 16;
export const selectMonitorPostScrollDelay = (state: RootState) => state.settings.monitorPostScrollDelay ?? 3000;
export const selectMonitorFontFamily = (state: RootState) => state.settings.monitorFontFamily ?? 'Roboto Condensed Variable, sans-serif';
export const selectSelectedHall = (state: RootState) => state.settings.selectedHall ?? 'all';
export const selectSendVisualControl = (state: RootState) => state.settings.sendVisualControl ?? false;
// Export reducer
export default settingsSlice.reducer;
